import { App } from 'App';
import { AppRegistrationSection } from '../../AppRegistrationSection';
import { ArrowLeftIcon, ClockIcon, OnlineBankIcon } from 'components/icons';
import { Frame } from 'components/organisms';
import { frameConfig } from '../../AppConfig';
import { getScreenTranslation } from '../../utils/translations';
import { IconElement } from 'types/IconElement';
import { Intent } from 'components/atoms/Card/Card';
import { JUICY_NEXT_BTN } from 'utils/constants';
import { Link, Spacer } from 'components';
import { routes } from '../../routes';
import { UploadDocumentationIcon } from 'components/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classes from 'classnames';
import OptionCardButton from 'components/molecules/OptionCardButton/OptionCardButton';
import React, { ReactNode, useMemo } from 'react';
import styles from './FirstLoanDocumentsRequired.module.scss';
import Text from 'components/atoms/Typography/Text';
import Title from 'components/atoms/Title/Title';
import variables from 'scss/variables.module.scss';

export default function FirstLoanDocumentsRequired() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const i18Next = useTranslation('verifyOptions');

  const isManualRoute = pathname === routes.FirstLoan_Manual;

  const { fetch: fetchPutCheckApplication } = App.useApi(
    'client/application/check',
    'PUT',
  );

  const handlerCardClick = (route: string) => {
    fetchPutCheckApplication();
    navigate(route);
  };

  type Options = {
    etiquette?: string;
    intent?: Intent;
    leftIcon: IconElement;
    onClick: () => void;
    subtitle: ReactNode;
    title: ReactNode;
    dataTest: string;
  };

  const manualOptions = [
    {
      etiquette: 'recomendado',
      intent: 'success',
      leftIcon: <OnlineBankIcon viewBox="0 0 73 64" />,
      onClick: () => {
        fetchPutCheckApplication();
        navigate(routes.FirstLoan_OnlineBank_Verify);
      },
      subtitle: (
        <span>
          {' '}
          <ClockIcon />
          <span>5min</span>
        </span>
      ),
      title: <span>{i18Next.t('verifyOptions:options.option1')}</span>,
      dataTest: 'onlineBankOption',
    },
    {
      leftIcon: <UploadDocumentationIcon />,
      onClick: () => handlerCardClick(routes.FirstLoan_Manual_Upload),
      subtitle: (
        <span>
          {' '}
          <ClockIcon />
          <span>{'>1h'}</span>
        </span>
      ),
      title: <span>{i18Next.t('verifyOptions:options.option2')}</span>,
      dataTest: 'uploadOption',
    },
  ];

  const contentDocRequired = useMemo(() => {
    if (search.includes('tinkReportFailed=true')) {
      return getScreenTranslation(
        'first_loan_docs_required_tinkFailed',
        i18Next,
      );
    }
    return getScreenTranslation('first_loan_documents_required', i18Next);
  }, [search]);

  const contentManual = getScreenTranslation('verifyOptions', i18Next);

  const optionsDocRequired = [
    {
      leftIcon: <UploadDocumentationIcon />,
      onClick: () => {
        navigate(routes.FirstLoan_OnlineBank_AddDocuments);
      },
      subtitle: (
        <span>
          {' '}
          <ClockIcon />
          <span>1h</span>
        </span>
      ),
      title: <span>{i18Next.t('verifyOptions:options.option2')}</span>,
      dataTest: 'uploadOption',
    },
  ];

  const content = isManualRoute ? contentManual : contentDocRequired;
  const onBackBtnHandler = isManualRoute
    ? () => navigate(routes.FirstLoan_OnlineBank_Verify)
    : undefined;

  const options: Options[] = isManualRoute ? manualOptions : optionsDocRequired;

  return (
    <Frame
      {...frameConfig}
      hideEditButton={true}
      sectionID={AppRegistrationSection.Confirmation}
      onBackBtnHandler={onBackBtnHandler}
      bodyWrapperClassName={variables['width-md']}
    >
      <Title text={content.title} data-test="css-Verify-Options-Title" />
      <Text
        className={styles['subtitle']}
        kind="p"
        dangerouslySetInnerHTML={{ __html: content.subtitle }}
        data-test="css-Info"
      />
      <div className={styles['option-cards']}>
        {options.map((args, index) => {
          return (
            <OptionCardButton
              {...args}
              key={`index-${index}`}
              className={classes([
                styles['option-card'],
                args.intent ? styles['intent-card'] : undefined,
                JUICY_NEXT_BTN,
              ])}
              dataTest={args.dataTest ?? 'xpath-Card'}
              classNameIconContainer={styles['option-card-icon']}
              classNameTitle={styles['option-card-title']}
            />
          );
        })}
      </div>
      {content.back_btn && onBackBtnHandler && (
        <Spacer
          marginTop={{ default: '40px' }}
          className={variables['desktop-only']}
        >
          <Link
            onClick={onBackBtnHandler}
            leftIcon={<ArrowLeftIcon />}
            dataTest="xpath-Back-Button"
          >
            {content.back_btn}
          </Link>
        </Spacer>
      )}
    </Frame>
  );
}
